import { getTableStyle, resolveRowKey, usePagedDataSource } from "./hooks";
import type { TableProps } from "./types";
import { twMerge } from "tailwind-merge";
import Pagination from "../Pagination";
import Divider from "../Divider";
import * as React from "react";
import {
  BodyRows,
  EmptyRow,
  HeaderRow,
  LoadingOverlay,
  TableScroller,
} from "./components";

function Table<T>({
  className,
  loading = false,
  dataSource,
  columns,
  pagination,
  locale,
  expandable,
  scroll,
  rowKey,
  onColumnClick,
}: TableProps<T>) {
  const rowKeyFn = React.useMemo(() => resolveRowKey<T>(rowKey), [rowKey]);
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);

  const [uncontrolledExpandedKeys, setUncontrolledExpandedKeys] =
    React.useState<React.Key[]>(() => expandable?.defaultExpandedRowKeys ?? []);

  const expandedKeys = expandable?.expandedRowKeys ?? uncontrolledExpandedKeys;
  const expandedSet = React.useMemo(
    () => new Set(expandedKeys),
    [expandedKeys],
  );

  const tableStyle = React.useMemo(() => getTableStyle(scroll), [scroll]);

  const paginationEnabled = !!Object.keys(pagination ?? {}).length;

  const pagedDataSource = usePagedDataSource(
    dataSource,
    paginationEnabled,
    pagination?.page ?? 1,
    pagination?.pageSize ?? dataSource.length,
  );

  const toggleExpand = (record: T, index: number) => {
    if (!expandable) return;
    const key = rowKeyFn(record, index);
    const isExpanded = expandedSet.has(key);
    const nextExpanded = !isExpanded;

    if (nextExpanded) {
      scrollerRef.current?.scrollTo({ left: 0, behavior: "auto" });
    }

    expandable.onExpand?.(nextExpanded, record);

    let nextKeys: React.Key[];

    if (expandable.accordion) {
      nextKeys = nextExpanded ? [key] : [];
    } else {
      const s = new Set(expandedKeys);
      if (nextExpanded) s.add(key);
      else s.delete(key);
      nextKeys = Array.from(s);
    }

    expandable.onExpandedRowKeysChange?.(nextKeys);

    if (!expandable.expandedRowKeys) {
      setUncontrolledExpandedKeys(nextKeys);
    }
  };

  return (
    <div className={twMerge("relative bg-surface", className)}>
      <TableScroller scroll={scroll} containerRef={scrollerRef}>
        <table
          className="min-w-full border-separate border-spacing-0 pb-[16px]"
          style={tableStyle}
        >
          <HeaderRow columns={columns} onClick={onColumnClick} />
          {!pagedDataSource.length ? (
            <tbody>
              <EmptyRow
                colSpan={columns.length + (expandable ? 1 : 0)}
                emptyText={loading ? "" : (locale?.emptyText ?? "No results")}
              />
            </tbody>
          ) : (
            <BodyRows
              rows={pagedDataSource}
              columns={columns}
              rowKeyFn={rowKeyFn}
              expandable={!!expandable}
              expandedSet={expandedSet}
              canExpandRow={(row) =>
                expandable?.rowExpandable ? expandable.rowExpandable(row) : true
              }
              onToggleExpand={toggleExpand}
              expandedRowRender={expandable?.expandedRowRender}
            />
          )}
        </table>
      </TableScroller>
      <LoadingOverlay show={loading} />
      {paginationEnabled && (
        <>
          <Divider />
          <Pagination
            {...pagination!}
            loading={loading}
            className={twMerge("mt-[16px]", pagination?.className)}
          />
        </>
      )}
    </div>
  );
}

export default Table;
